import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { Kicker } from './Kicker'
import { Button } from './Button'
import { Field, TextArea, TextInput } from './Field'
import { BottomSheet } from './BottomSheet'
import { supabase } from '@/lib/supabase'
import { useAppStore } from '@/stores/appStore'
import type { Person } from '@/lib/types'

interface Contact {
  id: string
  name: string
  relationship: string | null
  phone: string | null
  email: string | null
  notes: string | null
  priority: number
}

export function EmergencyContactsSection({ person }: { person: Person }) {
  const familyId = useAppStore((s) => s.familyId)
  const [editing, setEditing] = useState<Contact | 'new' | null>(null)

  const q = useQuery({
    enabled: !!familyId,
    queryKey: ['emergency-contacts', familyId, person.id],
    queryFn: async () => {
      const { data, error } = await supabase.from('emergency_contacts')
        .select('*').eq('family_id', familyId!)
        .or(`person_id.is.null,person_id.eq.${person.id}`)
        .order('priority', { ascending: true }).limit(20)
      if (error) throw error
      return (data ?? []) as Contact[]
    },
  })

  return (
    <section>
      <div className="flex items-center justify-between">
        <Kicker>Emergency contacts</Kicker>
        <button type="button" onClick={() => setEditing('new')} className="text-caption text-text-secondary underline">+ Add</button>
      </div>

      <div className="mt-2 space-y-2">
        {q.isLoading && <div className="glass-tile p-3 text-caption text-text-tertiary">Loading…</div>}
        {q.data?.length === 0 && <div className="glass-tile p-3 text-caption text-text-tertiary">No contacts on file. Tap +Add to record a grandparent, sitter, or neighbor.</div>}
        {q.data?.map((c, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.18, delay: Math.min(i * 0.03, 0.4) }}
            className="glass-tile p-3 flex items-center gap-3"
          >
            <div className="flex-1 min-w-0">
              <div className="text-body font-medium text-text-primary truncate">{c.name}</div>
              {c.relationship && <div className="text-caption text-text-tertiary">{c.relationship}</div>}
            </div>
            {c.phone && <a href={`tel:${c.phone}`} className="pill-btn pill-btn--mint shrink-0" style={{ minHeight: 34, padding: '6px 12px', fontSize: '12px' }}>Call</a>}
            <button type="button" onClick={() => setEditing(c)} className="text-caption text-text-tertiary shrink-0 underline">Edit</button>
          </motion.div>
        ))}
      </div>

      {editing && (
        <ContactSheet
          familyId={familyId!}
          personId={person.id}
          initial={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => q.refetch()}
        />
      )}
    </section>
  )
}

function ContactSheet({ familyId, personId, initial, onClose, onSaved }: { familyId: string; personId: string; initial: Contact | null; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(initial?.name ?? '')
  const [rel, setRel] = useState(initial?.relationship ?? '')
  const [phone, setPhone] = useState(initial?.phone ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [notes, setNotes] = useState(initial?.notes ?? '')
  const [priority, setPriority] = useState(initial?.priority ?? 5)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  async function save() {
    setBusy(true); setErr(null)
    try {
      const row = { family_id: familyId, person_id: personId, name: name.trim(), relationship: rel.trim() || null, phone: phone.trim() || null, email: email.trim() || null, notes: notes.trim() || null, priority }
      if (initial) {
        const { error } = await supabase.from('emergency_contacts').update(row).eq('id', initial.id)
        if (error) throw new Error(error.message)
      } else {
        const { error } = await supabase.from('emergency_contacts').insert(row)
        if (error) throw new Error(error.message)
      }
      onSaved(); onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally { setBusy(false) }
  }

  async function remove() {
    if (!initial || !window.confirm(`Remove ${initial.name}?`)) return
    setBusy(true)
    try {
      const { error } = await supabase.from('emergency_contacts').delete().eq('id', initial.id)
      if (error) throw new Error(error.message)
      onSaved(); onClose()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally { setBusy(false) }
  }

  return (
    <BottomSheet
      open={true}
      onClose={onClose}
      title={initial ? 'Edit contact' : 'Add contact'}
      action={<Button variant="primary" size="lg" loading={busy} onClick={save} className="w-full">Save</Button>}
    >
      <div className="space-y-3 pt-3">
        <Field label="Name"><TextInput value={name} onChange={(e) => setName(e.target.value)} autoFocus required /></Field>
        <Field label="Relationship" hint="e.g. Grandma, Sitter, Neighbor"><TextInput value={rel} onChange={(e) => setRel(e.target.value)} /></Field>
        <Field label="Phone" hint="With country code"><TextInput type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} /></Field>
        <Field label="Email"><TextInput type="email" value={email} onChange={(e) => setEmail(e.target.value)} /></Field>
        <Field label="Priority" hint="1 = first to call, 9 = last"><TextInput type="number" min={1} max={9} value={String(priority)} onChange={(e) => setPriority(parseInt(e.target.value, 10) || 5)} /></Field>
        <Field label="Notes"><TextArea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        {initial && (
          <button type="button" onClick={remove} className="text-caption underline" style={{ color: '#D14758' }}>
            Remove contact
          </button>
        )}
        {err && <div className="rounded-r-sm p-3 text-caption" style={{ background: 'rgba(232,94,110,0.12)', color: '#D14758' }}>{err}</div>}
      </div>
    </BottomSheet>
  )
}
